<template>
  <div
    class="product-list-page"
    :class="{ 'page-drag-over': isPageDraggingOver }"
    @dragenter.prevent="handlePageDragEnter"
    @dragover.prevent="handlePageDragOver"
    @dragleave.prevent="handlePageDragLeave"
    @drop.prevent="handlePageDrop"
  >
    <div
      v-if="isPageDraggingOver"
      class="page-drop-overlay"
    >
      <div class="page-drop-message">
        <i class="fas fa-file-upload fa-4x mb-3" />
        <h3>Drop spreadsheet file here</h3>
        <p class="text-muted">
          Supports .xlsx, .xls, and .csv files
        </p>
      </div>
    </div>

    <b-form>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="List Name"
            label-for="name"
            class="product-id-list-form-group"
          >
            <b-form-input
              id="name"
              v-model="formData.name"
              type="text"
              placeholder="Enter list name"
              class="product-id-list-name-input"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="product-id-list-section-title">
              Product Records
            </h5>
            <div class="d-flex align-items-center">
              <b-button
                variant="outline-secondary"
                class="product-id-list-import-btn"
                @click="showImportModal"
              >
                <i class="fas fa-upload mr-2" />
                Import from Spreadsheet
              </b-button>
              <b-dropdown
                variant="success"
                class="ml-2 product-id-list-add-dropdown"
                text="Add Products"
                right
              >
                <b-dropdown-item @click="addNewRecord(1)">
                  Add Product
                </b-dropdown-item>
                <b-dropdown-item @click="addMultipleIdentifiers">
                  Add Multiple...
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div class="table-responsive product-id-list-table-wrapper">
            <table class="table product-id-list-table">
              <thead class="product-id-list-table-head">
                <tr>
                  <th class="product-id-list-table-th">
                    Row #
                  </th>
                  <th class="product-id-list-table-th">
                    Material Code
                  </th>
                  <th class="product-id-list-table-th">
                    GTIN
                  </th>
                  <th class="product-id-list-table-th">
                    ASIN
                  </th>
                  <th class="product-id-list-table-th">
                    TCIN
                  </th>
                  <th class="product-id-list-table-th">
                    Walmart ID
                  </th>
                  <th class="product-id-list-table-th">
                    Salsify ID
                  </th>
                  <th class="product-id-list-table-th">
                    URL
                  </th>
                  <th class="product-id-list-table-th">
                    Brand
                  </th>
                  <th class="product-id-list-table-th">
                    Product Name
                  </th>
                  <th class="product-id-list-table-th">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(record, index) in productRecords"
                  :key="index"
                  class="product-id-list-table-row"
                >
                  <td class="product-id-list-table-td">
                    {{ index + 1 }}
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.material_code"
                      size="sm"
                      placeholder="Material Code"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.gtin"
                      size="sm"
                      placeholder="GTIN"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.asin"
                      size="sm"
                      placeholder="ASIN"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.tcin"
                      size="sm"
                      placeholder="TCIN"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.walmart_id"
                      size="sm"
                      placeholder="Walmart ID"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.salsify_id"
                      size="sm"
                      placeholder="Salsify ID"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.url"
                      size="sm"
                      placeholder="URL"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.brand"
                      size="sm"
                      placeholder="Brand"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-form-input
                      v-model="record.product_name"
                      size="sm"
                      placeholder="Product Name"
                      class="product-id-list-input"
                    />
                  </td>
                  <td class="product-id-list-table-td">
                    <b-button
                      variant="danger"
                      size="sm"
                      class="product-id-list-delete-btn"
                      @click="removeRecord(index)"
                    >
                      <i class="far fa-trash-alt" />
                    </b-button>
                  </td>
                </tr>
                <tr v-if="productRecords.length === 0">
                  <td
                    colspan="11"
                    class="product-id-list-empty-state-td"
                  >
                    No records added yet. Click "Add Product" to get started.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <div class="d-flex justify-content-end product-id-list-footer-actions">
            <b-button
              variant="outline-secondary"
              class="mr-2 product-id-list-cancel-btn"
              @click="handleCancel"
            >
              Cancel
            </b-button>
            <b-button
              variant="success"
              class="product-id-list-save-btn"
              :disabled="isSubmitting"
              @click="handleSubmit"
            >
              {{ isSubmitting ? 'Saving...' : 'Save List' }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>

    <!-- Import Modal -->
    <b-modal
      id="import-modal"
      ref="importModal"
      title="Import Products from Spreadsheet"
      size="lg"
      body-class="p-0"
      footer-class="p-0"
      hide-footer
    >
      <div
        class="drop-zone-wrapper"
        :class="{ 'drag-over': isDraggingOver }"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
      >
        <div class="modal-body-content">
          <b-form @submit.prevent="handleImport">
            <b-form-group label="Select Spreadsheet file">
              <b-form-file
                v-model="importFile"
                accept=".xlsx,.xls,.csv"
                placeholder="Choose file or drop it here..."
                drop-placeholder="Drop file here..."
                @input="onFileInput"
              />
              <small class="form-text text-muted">
                Need a template?
                <a
                  href="/product-id-list/import-template"
                  download
                  class="text-primary"
                >
                  <i class="fas fa-download" /> Download sample spreadsheet
                </a>
              </small>
            </b-form-group>

            <div
              v-if="importFile"
              class="mt-3"
            >
              <p class="text-muted">
                <strong>Selected file:</strong> {{ importFile.name }}
              </p>
            </div>

            <div
              v-if="fileSheets.length > 0"
              class="mt-3"
            >
              <p class="">
                <strong>Sheets:</strong>
              </p>
              <b-form-group>
                <b-form-checkbox-group v-model="selectedSheets">
                  <b-form-checkbox
                    v-for="sheet in fileSheets"
                    :key="sheet"
                    :value="sheet"
                  >
                    {{ sheet }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </b-form-group>
            </div>


            <div
              v-if="selectedSheets.length > 0"
              class="mt-3 column-mapping-wrapper"
            >
              <b-button
                v-b-toggle.import-column-mapping
                variant="link"
                class="p-0 column-mapping-toggle"
              >
                <i class="fas fa-random mr-1" />
                Column mapping
                <small class="text-muted">(optional)</small>
              </b-button>

              <b-collapse
                id="import-column-mapping"
                class="mt-3"
              >
                <div class="column-mapping-panel">
                  <p class="text-muted small mb-3">
                    Match spreadsheet columns to Product ID List fields.
                    Suggested mappings are selected automatically.
                  </p>

                  <div
                    v-for="sheetName in selectedSheets"
                    :key="sheetName"
                    class="column-mapping-sheet"
                  >
                    <h6
                      v-if="selectedSheets.length > 1"
                      class="mb-3"
                    >
                      {{ sheetName }}
                    </h6>

                    <template v-if="columnMappings[sheetName]">
                      <b-form-group
                        v-for="field in importMappingFields"
                        :key="sheetName + '-' + field.key"
                        :label="field.label"
                        label-cols-md="4"
                        label-align-md="right"
                        class="mb-2"
                      >
                        <b-form-select
                          v-model="columnMappings[sheetName][field.key]"
                          :options="getColumnOptions(sheetName)"
                          size="sm"
                        />
                      </b-form-group>
                    </template>

                    <p
                      v-else
                      class="text-muted small"
                    >
                      No columns found in this sheet.
                    </p>
                  </div>
                </div>
              </b-collapse>
            </div>
          </b-form>
        </div>

        <div class="drop-zone-footer">
          <b-button
            variant="secondary"
            @click="cancelImport"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="!importFile"
            @click="handleImport"
          >
            Import Products
          </b-button>
        </div>

        <div
          v-if="isDraggingOver"
          class="drop-zone-overlay"
        >
          <div class="drop-zone-message">
            <i class="fas fa-file-upload fa-3x mb-3" />
            <h4>Drop spreadsheet file here</h4>
            <p class="text-muted">
              Supports .xlsx, .xls, and .csv files
            </p>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Add Multiple Identifiers Modal -->
    <b-modal
      id="add-multiple-identifiers-modal"
      ref="addMultipleIdentifiersModal"
      title="Add Multiple Product Identifiers"
      size="lg"
    >
      <b-form @submit.prevent="confirmAddMultipleIdentifiers">
        <b-form-group
          label="Paste each product identifier on a new line (e.g. URL, ASIN, GTIN, etc.):"
          label-for="identifiersText"
        >
          <b-form-textarea
            id="identifiersText"
            v-model="identifiersText"
            rows="10"
            placeholder="Enter each product identifier on a new line, separated by line breaks..."
            @input="updateIdentifiersPreview"
          />
        </b-form-group>

        <div
          v-if="identifiersPreview.length > 0"
          class="mt-3 border rounded p-2"
          style="max-height: 340px; overflow-y: auto;"
        >
          <p class="text-success mb-2">
            <strong>{{ identifiersPreview.length }} identifiers detected:</strong>
          </p>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(item, index) in identifiersPreview"
              :key="index"
              class="mb-1"
            >
              <span class="badge badge-primary mr-2">{{ item.type.toUpperCase() }}</span>
              <code>{{ item.value }}</code>
            </li>
          </ul>
        </div>

        <div
          v-if="identifiersErrors.length > 0"
          class="mt-3"
        >
          <p class="text-warning mb-2">
            <strong>Unrecognized formats:</strong>
          </p>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(error, index) in identifiersErrors"
              :key="index"
              class="mb-1 text-muted"
            >
              <code>{{ error }}</code>
            </li>
          </ul>
        </div>
      </b-form>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <div class="d-flex align-items-center">
            <b-button
              variant="secondary"
              @click="cancelAddMultipleIdentifiers"
            >
              Cancel
            </b-button>
            <b-form-checkbox
              v-model="addGtinsAsSalsifyIds"
              class="ml-2 mb-0"
            >
              <span class="small">
                Add GTINs as Salsify IDs
              </span>
            </b-form-checkbox>
          </div>
          <b-button
            variant="success"
            :disabled="identifiersPreview.length === 0"
            @click="confirmAddMultipleIdentifiers"
          >
            Add {{ identifiersPreview.length }} Record{{ identifiersPreview.length !== 1 ? 's' : '' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ProductIdListForm',
  props: {
    productIdList: {
      type: String,
      default: '{}'
    }
  },
  data() {
    return {
      formData: {
        name: '',
        recordsToUpdate: [],
        recordsToDelete: [],
        recordsToAdd: [],
      },
      productRecords: [],
      productIdListObject: null,
      isSubmitting: false,
      importFile: null,
      deletedRecordIds: [],
      identifiersText: '',
      identifiersPreview: [],
      identifiersErrors: [],
      fileSheets: [],
      selectedSheets: [],
      sheetHeaders: {},
      columnMappings: {},
      importMappingFields: [
        {
          key: 'material_code',
          label: 'Material Code',
          aliases: [
            'material code',
          ],
        },
        {
          key: 'gtin',
          label: 'GTIN',
          aliases: ['gtin'],
        },
        {
          key: 'asin',
          label: 'ASIN',
          aliases: ['asin', 'amazon asin', 'amazon.com asin', 'amazon_asin'],
        },
        {
          key: 'tcin',
          label: 'TCIN',
          aliases: ['tcin', 'target tcin', 'target_tcin'],
        },
        {
          key: 'walmart_id',
          label: 'Walmart ID',
          aliases: ['walmart id', 'walmart.com id', 'walmart_id'],
        },
        {
          key: 'salsify_id',
          label: 'Salsify ID',
          aliases: ['salsify id', 'salsify_id'],
        },
        {
          key: 'url',
          label: 'URL',
          aliases: ['url', 'link', 'website'],
        },
        {
          key: 'brand',
          label: 'Brand',
          aliases: ['brand', 'brand name'],
        },
        {
          key: 'product_name',
          label: 'Product Name',
          aliases: ['product name', 'product_name', 'product title', 'title'],
        },
      ],
      isDraggingOver: false,
      dragCounter: 0,
      isPageDraggingOver: false,
      pageDragCounter: 0,
      addGtinsAsSalsifyIds: true,
    };
  },
  watch: {
    fileSheets(newItems) {
      this.selectedSheets = [...newItems];
    },
    selectedSheets(newSheets) {
      newSheets.forEach(sheetName => {
        this.ensureColumnMappings(sheetName);
      });
    },
    productRecords: {
      handler(records) {
        records.forEach(record => {
          if (record.url && record.url.trim()) {
            this.extractIdsFromUrl(record);
          }
        });
      },
      deep: true
    }
  },
  mounted() {
    if (this.productIdList) {
      try {
        this.productIdListObject = JSON.parse(this.productIdList);
        this.formData.name = this.productIdListObject.name || '';
        this.productRecords = this.productIdListObject.product_id_list_records || [];
      } catch (error) {
        console.error('Error parsing product ID list:', error);
      }
    }
  },
  methods: {
    handleDragEnter() {
      this.dragCounter++;
      this.isDraggingOver = true;
    },

    handleDragOver(e) {
      e.dataTransfer.dropEffect = 'copy';
      this.isDraggingOver = true;
    },

    handleDragLeave() {
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDraggingOver = false;
      }
    },

    handleDrop(e) {
      this.dragCounter = 0;
      this.isDraggingOver = false;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const validExtensions = ['.xlsx', '.xls', '.csv'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (validExtensions.includes(fileExtension)) {
          this.importFile = file;
          this.onFileInput(file);
        } else {
          toastr.error('Please drop a valid spreadsheet file (.xlsx, .xls, or .csv)');
        }
      }
    },

    handlePageDragEnter(e) {
      if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
        this.pageDragCounter++;
        this.isPageDraggingOver = true;
      }
    },

    handlePageDragOver(e) {
      if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
        e.dataTransfer.dropEffect = 'copy';
        this.isPageDraggingOver = true;
      }
    },

    handlePageDragLeave() {
      this.pageDragCounter--;
      if (this.pageDragCounter === 0) {
        this.isPageDraggingOver = false;
      }
    },

    async handlePageDrop(e) {
      this.pageDragCounter = 0;
      this.isPageDraggingOver = false;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const validExtensions = ['.xlsx', '.xls', '.csv'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (validExtensions.includes(fileExtension)) {
          this.importFile = file;
          await this.onFileInput(file);

          if (this.fileSheets.length > 0) {
            this.$refs.importModal.show();
          } else {
            await this.handleImport();
          }
        } else {
          toastr.error('Please drop a valid spreadsheet file (.xlsx, .xls, or .csv)');
        }
      }
    },

    addNewRecord(count = 1) {
      for (let i = 0; i < count; i++) {
        this.productRecords.push({
          material_code: '',
          gtin: '',
          asin: '',
          tcin: '',
          walmart_id: '',
          salsify_id: '',
          url: '',
          brand: '',
          product_name: '',
        });
      }
    },
    removeRecord(index) {
      const recordToRemove = this.productRecords[index];

      if (recordToRemove && recordToRemove.id) {
        this.deletedRecordIds.push(recordToRemove.id);
      }

      this.productRecords.splice(index, 1);
    },
    showImportModal() {
      this.resetImportState();
      this.$refs.importModal.show();
    },
    cancelImport() {
      this.resetImportState();
      this.$refs.importModal.hide();
    },
    async handleImport() {
      if (!this.importFile) {
        toastr.error('Please select a file to import');
        return;
      }

      if (this.fileSheets.length > 0 && this.selectedSheets.length === 0) {
        toastr.error('Please select at least one sheet to import from.');
        return;
      }

      try {
        const cleanedFile = await this.cleanExcelFile(this.importFile);

        const formData = new FormData();
        formData.append('file', cleanedFile);

        for (const [idx, sheet] of this.fileSheets.entries()) {
          if (!this.selectedSheets.includes(sheet)) continue;
          formData.append(`sheets[${idx}]`, sheet);
        }

        if (this.productIdListObject && this.productIdListObject.id) {
          formData.append('productIdListId', this.productIdListObject.id);
        }

        const response = await axios.post('/product-id-list/preview-import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          const newRecords = response.data.records || [];

          if (newRecords.length === 0) {
            toastr.warning('No valid product records found in the file. Please check the file format and column names (GTIN, ASIN, etc.)');
          } else {
            this.productRecords = [...this.productRecords, ...newRecords];
            toastr.success(`Previewed ${newRecords.length} records. Click "Save List" to save them to the database.`);
          }

          this.$refs.importModal.hide();
          this.resetImportState();
        } else {
          toastr.error(response.data.message);
        }
      } catch (error) {
        console.error('Import error:', error);
        if (error.response && error.response.data) {
          toastr.error('Import failed: ' + (error.response.data.message || 'Unknown error'));
        } else {
          toastr.error('Failed to import products. Please try again.');
        }
      }
    },
    async handleSubmit(event) {
      if (event) {
        event.preventDefault();
      }

      this.isSubmitting = true;

      try {
        // Validate records before submission
        const validationResult = this.validateRecords();
        if (!validationResult.valid) {
          toastr.error(validationResult.message);

          return;
        }

        // Prepare records for submission - filter out empty records first
        const filteredRecords = this.productRecords.filter(record =>
          record.material_code || record.gtin || record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url
        );

        // Clear all record arrays
        this.formData.recordsToAdd = [];
        this.formData.recordsToUpdate = [];
        this.formData.recordsToDelete = [];

        // Restore records marked for deletion
        this.formData.recordsToDelete = [...this.deletedRecordIds];

        if (this.productIdListObject && this.productIdListObject.id) {
          // Edit mode: separate existing vs new records
          this.formData.recordsToUpdate = filteredRecords.filter(record => record.id);
          this.formData.recordsToAdd = filteredRecords.filter(record => !record.id);
        } else {
          // Create mode: all records are new
          this.formData.recordsToAdd = filteredRecords;
        }

        const url = this.productIdListObject && this.productIdListObject.id
          ? `/product-id-list/${this.productIdListObject.id}/edit`
          : '/product-id-list/create';

        const response = await axios.post(url, this.formData);

        if (response.data.productIdList) {
          toastr.success('Product list saved successfully!');
          // Redirect to view page
          window.location.href = `/product-id-list/${response.data.productIdList.id}`;
        }
      } catch (error) {
        console.error('Error saving product ID list:', error);
        if (error.response && error.response.data && error.response.data.message) {
          toastr.error('Error: ' + error.response.data.message);
        } else {
          toastr.error('Failed to save product list. Please try again.');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    validateRecords() {
      const seen = new Map();

      for (let i = 0; i < this.productRecords.length; i++) {
        const record = this.productRecords[i];

        // Check if record has at least one field filled
        const hasData = record.material_code || record.gtin || record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;
        if (!hasData) {
          continue; // Skip empty records
        }

        // Validate individual fields
        const fieldValidation = this.validateRecordFields(record, i + 1);
        if (!fieldValidation.valid) {
          return fieldValidation;
        }

        // Check for duplicates
        const key = this.createRecordKey(record);

        if (key && seen.has(key)) {
          const originalRow = seen.get(key);

          const duplicateInfo = this.parseDuplicateKey(key);

          return {
            valid: false,
            message: `Duplicate product identifiers found in row ${i + 1}: ${duplicateInfo} already exists in row ${originalRow}`
          };
        }
        if (key) {
          seen.set(key, i + 1);
        }
      }

      return { valid: true };
    },
    validateRecordFields(record, rowNumber) {
      // Validate ASIN
      if (record.asin && !/^B[A-Z0-9]{9}$/i.test(record.asin)) {
        return { valid: false, message: `Row ${rowNumber}: ASIN must start with B followed by 9 alphanumeric characters` };
      }

      // Validate GTIN
      if (record.gtin && !/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(record.gtin)) {
        return { valid: false, message: `Row ${rowNumber}: GTIN must be 8, 12, 13, or 14 digits` };
      }

      // Validate TCIN
      if (record.tcin && !/^A-\d{8,9}$/i.test(record.tcin)) {
        return { valid: false, message: `Row ${rowNumber}: TCIN must be in format A-XXXXXXXX` };
      }

      // Validate URL
      if (record.url) {
        try {
          new URL(record.url);
        } catch (e) {
          return { valid: false, message: `Row ${rowNumber}: Invalid URL format` };
        }
      }

      return { valid: true };
    },
    createRecordKey(record) {
      const keys = [];
      if (record.asin) keys.push('asin:' + record.asin);
      if (record.gtin) keys.push('gtin:' + record.gtin);
      if (record.tcin) keys.push('tcin:' + record.tcin);
      if (record.walmart_id) keys.push('walmart_id:' + record.walmart_id);
      if (record.salsify_id) keys.push('salsify_id:' + record.salsify_id);
      if (record.material_code) keys.push('material_code:' + record.material_code);
      return keys.length > 0 ? keys.join('|') : null;
    },

    parseDuplicateKey(key) {
      const parts = key.split('|');

      const identifiers = parts.map(part => {
        const [type, value] = part.split(':');

        const displayNames = {
          'asin': 'ASIN',
          'gtin': 'GTIN',
          'tcin': 'TCIN',
          'walmart_id': 'Walmart ID',
          'salsify_id': 'Salsify ID',
          'material_code': 'Material Code'
        };

        return `${displayNames[type] || type} '${value}'`;
      });

      return identifiers.length > 1
        ? identifiers.slice(0, -1).join(', ') + ' and ' + identifiers[identifiers.length - 1]
        : identifiers[0];
    },

    detectIdentifierType(identifier) {
      const trimmed = identifier.trim();

      if (!trimmed) return null;

      // 1. URLs
      if (trimmed.match(/^https?:\/\//i) ||
        trimmed.match(/\b(?:amazon\.com|target\.com|walmart\.com)\b/i)) {
        return 'url';
      }

      // 2. TCIN (specific format: A-########)
      if (/^A-\d{8,9}$/i.test(trimmed)) {
        return 'tcin';
      }

      // 3. ASIN (starts with B + 9 alphanumeric characters)
      if (/^B[A-Z0-9]{9}$/i.test(trimmed)) {
        return 'asin';
      }

      // 4. GTIN (8, 12, 13, or 14 digits only)
      if (/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(trimmed)) {
        return 'gtin';
      }

      // 5. Walmart ID (1-11 digits only, catch remaining numeric)
      if (/^\d{1,11}$/.test(trimmed)) {
        return 'walmart_id';
      }

      // Unrecognized format
      return null;
    },

    parseIdentifiersFromText(text) {
      if (!text) return { identifiers: [], errors: [] };

      const lines = text
        .split(/[\r\n,\t]+/)
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const identifiers = [];
      const errors = [];

      lines.forEach(line => {
        const type = this.detectIdentifierType(line);
        if (type) {
          identifiers.push({
            value: line,
            type: type
          });
        } else {
          errors.push(line);
        }
      });

      return { identifiers, errors };
    },

    updateIdentifiersPreview() {
      const result = this.parseIdentifiersFromText(this.identifiersText);
      this.identifiersPreview = result.identifiers;
      this.identifiersErrors = result.errors;
    },

    addMultipleIdentifiers() {
      this.identifiersText = '';
      this.identifiersPreview = [];
      this.identifiersErrors = [];
      this.$refs.addMultipleIdentifiersModal.show();
    },

    confirmAddMultipleIdentifiers() {
      if (this.identifiersPreview.length === 0) {
        toastr.error('No valid identifiers detected');
        return;
      }

      this.identifiersPreview.forEach(item => {
        const newRecord = {
          material_code: '',
          gtin: '',
          asin: '',
          tcin: '',
          walmart_id: '',
          salsify_id: '',
          url: '',
        };

        newRecord[item.type] = item.value;

        if (this.addGtinsAsSalsifyIds && item.type === 'gtin') {
          newRecord.salsify_id = item.value;
        }

        this.productRecords.push(newRecord);
      });

      const typeCount = {};
      this.identifiersPreview.forEach(item => {
        typeCount[item.type] = (typeCount[item.type] || 0) + 1;
      });

      const typeSummary = Object.entries(typeCount)
        .map(([type, count]) => `${count} ${type.toUpperCase()}${count > 1 ? 's' : ''}`)
        .join(', ');

      toastr.success(`Added ${this.identifiersPreview.length} records: ${typeSummary}`);

      this.$refs.addMultipleIdentifiersModal.hide();
    },

    cancelAddMultipleIdentifiers() {
      this.identifiersText = '';
      this.identifiersPreview = [];
      this.identifiersErrors = [];
      this.$refs.addMultipleIdentifiersModal.hide();
    },

    normalizeMappingHeader(header) {
      return String(header || '')
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');
    },

    getColumnOptions(sheetName) {
      const columns = this.sheetHeaders[sheetName] || [];

      return [
        {
          value: null,
          text: 'Not mapped',
        },
        ...columns.map(column => ({
          value: column.index,
          text: column.label,
        })),
      ];
    },

    findAutomaticColumnMapping(sheetName, field) {
      const columns = this.sheetHeaders[sheetName] || [];

      for (const alias of field.aliases) {
        const normalizedAlias = this.normalizeMappingHeader(alias);
        const column = columns.find(item =>
          this.normalizeMappingHeader(item.originalLabel) === normalizedAlias
        );

        if (column) {
          return column.index;
        }
      }

      return null;
    },

    ensureColumnMappings(sheetName) {
      if (this.columnMappings[sheetName]) {
        return;
      }

      const mapping = {};

      this.importMappingFields.forEach(field => {
        mapping[field.key] = this.findAutomaticColumnMapping(sheetName, field);
      });

      this.$set(this.columnMappings, sheetName, mapping);
    },

    resetImportState() {
      this.importFile = null;
      this.fileSheets = [];
      this.selectedSheets = [];
      this.sheetHeaders = {};
      this.columnMappings = {};
    },

    async onFileInput(files) {
      const file = Array.isArray(files) ? files[0] : files;

      this.fileSheets = [];
      this.selectedSheets = [];
      this.sheetHeaders = {};
      this.columnMappings = {};

      if (!file) {
        return;
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetHeaders = {};

            workbook.SheetNames.forEach(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                raw: false,
                defval: '',
              });
              const headers = rows[0] || [];

              sheetHeaders[sheetName] = headers.map((header, index) => {
                const originalLabel = String(header || '').trim();

                return {
                  index,
                  originalLabel,
                  label: originalLabel || `Column ${index + 1}`,
                };
              });
            });

            this.sheetHeaders = sheetHeaders;
            this.fileSheets = [...workbook.SheetNames];
            this.selectedSheets = [...workbook.SheetNames];

            this.selectedSheets.forEach(sheetName => {
              this.ensureColumnMappings(sheetName);
            });

            resolve();
          } catch (error) {
            console.error('Error reading spreadsheet:', error);
            reject(error);
          }
        };

        reader.onerror = error => {
          console.error('Error reading file:', error);
          reject(error);
        };

        reader.readAsArrayBuffer(file);
      });
    },

    async cleanExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            workbook.SheetNames.forEach(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                raw: false,
                defval: '',
              });

              if (rows.length === 0) {
                return;
              }

              const headerCounts = {};
              const cleanedHeaders = (rows[0] || []).map((header, index) => {
                if (!header || String(header).trim() === '') {
                  return `column_${index}`;
                }

                let normalizedHeader = String(header).trim().toLowerCase();

                if (
                  normalizedHeader.includes('gtin') &&
                  normalizedHeader !== 'gtin'
                ) {
                  normalizedHeader = normalizedHeader.replace('gtin', 'product_code');
                }

                if (
                  normalizedHeader.includes('asin') &&
                  normalizedHeader !== 'asin' &&
                  normalizedHeader !== 'amazon.com asin' &&
                  normalizedHeader !== 'amazon_asin'
                ) {
                  normalizedHeader = normalizedHeader.replace('asin', 'product_code');
                }

                if (!headerCounts[normalizedHeader]) {
                  headerCounts[normalizedHeader] = 1;
                  return normalizedHeader;
                }

                headerCounts[normalizedHeader]++;
                return `${normalizedHeader}_${headerCounts[normalizedHeader]}`;
              });

              const sheetMapping = this.columnMappings[sheetName] || {};
              const activeMappings = Object.entries(sheetMapping)
                .filter(([, columnIndex]) =>
                  columnIndex !== null &&
                  columnIndex !== undefined &&
                  columnIndex !== ''
                )
                .map(([targetField, columnIndex]) => ({
                  targetField,
                  columnIndex: Number(columnIndex),
                }));

              rows[0] = [
                ...cleanedHeaders,
                ...activeMappings.map(mapping => mapping.targetField),
              ];

              for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
                const sourceRow = rows[rowIndex] || [];

                rows[rowIndex] = [
                  ...sourceRow,
                  ...activeMappings.map(mapping =>
                    sourceRow[mapping.columnIndex] ?? ''
                  ),
                ];
              }

              workbook.Sheets[sheetName] = XLSX.utils.aoa_to_sheet(rows);
            });

            const cleanedData = XLSX.write(workbook, {
              type: 'array',
              bookType: 'xlsx',
            });
            const cleanedFileName = file.name.replace(/\.(xlsx|xls|csv)$/i, '') + '.xlsx';
            const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([cleanedData], { type: mimeType });

            resolve(new File([blob], cleanedFileName, { type: mimeType }));
          } catch (error) {
            console.error('Error cleaning Excel file:', error);
            reject(error);
          }
        };

        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    extractIdsFromUrl(record) {
      const url = record.url.trim();

      // Extract Walmart ID from Walmart URL
      const walmartMatch = url.match(/walmart\.com\/ip\/[^/]+\/(\d+)/i);
      if (walmartMatch && walmartMatch[1] && !record.walmart_id) {
        const extractedId = walmartMatch[1];
        // Validate using existing pattern (1-11 digits)
        if (/^\d{1,11}$/.test(extractedId)) {
          record.walmart_id = extractedId;
        }
      }

      // Extract Amazon ASIN from Amazon URL
      const amazonMatch = url.match(/amazon\.com\/(?:[^/]+\/)?dp\/([A-Z0-9]{10})(?:\/|\?|$)/i);
      if (amazonMatch && amazonMatch[1] && !record.asin) {
        const extractedAsin = amazonMatch[1];
        // Validate using existing ASIN pattern (B + 9 alphanumeric)
        if (/^B[A-Z0-9]{9}$/i.test(extractedAsin)) {
          record.asin = extractedAsin;
        }
      }
    },

    handleCancel() {
      window.history.back();
    }
  }
};
</script>

<style scoped>
.drop-zone-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.modal-body-content {
  padding: 1rem;
}

.drop-zone-wrapper.drag-over {
  background-color: rgba(0, 123, 255, 0.05);
  border: 2px dashed #209c86;
}

.drop-zone-footer {
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid #dee2e6;
}

.drop-zone-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
}

.drop-zone-message {
  text-align: center;
  color: #209c86;
}

.drop-zone-message i {
  color: #209c86;
}

.drop-zone-message h4 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.drop-zone-message p {
  margin-bottom: 0;
}

.product-list-page {
  position: relative;
  min-height: 100vh;
  isolation: isolate;
}

.page-drag-over {
  position: relative;
}

.page-drop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  backdrop-filter: blur(2px);
}

.page-drop-message {
  text-align: center;
  color: #209c86;
  padding: 40px;
  border: 3px dashed #209c86;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
}

.page-drop-message i {
  color: #209c86;
}

.page-drop-message h3 {
  color: #209c86;
  font-weight: 600;
  margin-bottom: 10px;
}

.page-drop-message .text-muted {
  color: #666 !important;
  font-size: 14px;
}

.column-mapping-toggle {
  color: #209c86;
  font-weight: 600;
  text-decoration: none;
}

.column-mapping-toggle:hover {
  color: #187764;
  text-decoration: none;
}

.column-mapping-panel {
  padding: 16px;
  background: #f8fafb;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.column-mapping-sheet + .column-mapping-sheet {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

.small {
  font-size: 12px;
}
</style>
